import React from 'react';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import AnimatedSection from '../../components/common/AnimatedSection';

// 样式组件
const OverviewContainer = styled('div', {
  padding: '0 0px',
});

const BlogList = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
  gap: '30px',
  margin: '10px 0',
  
  '@media (max-width: 640px)': {
    gridTemplateColumns: '1fr',
  }
});

const BlogCard = styled('div', {
  borderRadius: '15px',
  overflow: 'hidden',
  boxShadow: '0 5px 15px rgba(0, 0, 0, 0.05)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  backgroundColor: 'white',
  
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
  },
  
  '& .blog-image': {
    width: '100%',
    height: '200px',
    backgroundColor: '#263155',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: '500',
    
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    }
  },
  
  '& .blog-content': {
    padding: '25px',
  },
  
  '& .blog-date': {
    fontSize: '14px',
    color: '#6B7280',
    marginBottom: '10px',
  },
  
  '& .blog-title': {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: '15px',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  
  '& .blog-excerpt': {
    fontSize: '15px',
    color: '#4B5563',
    lineHeight: '1.6',
    marginBottom: '20px',
    display: '-webkit-box',
    '-webkit-line-clamp': 3,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  
  '& .blog-link': {
    display: 'inline-block',
    marginTop: '15px',
    color: '#3563E9',
    fontWeight: '500',
    textDecoration: 'none',
    
    '&:hover': {
      textDecoration: 'underline',
    }
  }
});

const LoadMoreButton = styled('button', {
  display: 'block',
  margin: '40px auto',
  padding: '12px 30px',
  borderRadius: '30px',
  background: 'linear-gradient(90deg, #3563E9 0%, #6C5CE7 100%)',
  color: 'white',
  fontWeight: '500',
  border: 'none',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  
  '&:hover': {
    opacity: 0.9,
    transform: 'translateY(-2px)',
    boxShadow: '0 5px 15px rgba(108, 92, 231, 0.4)',
  }
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ImagePlaceholder = styled('div', {
  width: '100%',
  height: '100%',
  backgroundColor: '#263155',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  fontSize: '18px',
  fontWeight: '500',
});

// 博客概览页面组件
const NewsOverview: React.FC = () => {
  const { t } = useTranslation();
  
  // 示例博客数据
  const blogItems = [
    {
      id: '1',
      title: "How Does Electricity Affect Your Mining Profits?",
      excerpt: "Let's break down one of the most important factors in determining mining profitability",
      date: "18.02.2025",
      image: "/images/blog/blog1.jpeg"
    },
    {
      id: '2',
      title: "Compare Bitcoin Mining Hardware: What's Best in 2025?",
      excerpt: "Find out which mining hardware delivers the best ROI in today's market",
      date: "10.02.2025",
      image: "/images/blog/blog2.jpeg"
    },
    {
      id: '3',
      title: "The Future of Cloud Mining: Predictions for Next Year",
      excerpt: "Industry experts share their insights on where cloud mining is headed",
      date: "05.02.2025",
      image: "/images/blog/blog3.jpeg"
    },
    {
      id: '4',
      title: "5 Ways to Reduce Your Mining Carbon Footprint",
      excerpt: "Sustainable practices that can make your mining operation more eco-friendly",
      date: "28.01.2025",
      image: "/images/blog/blog4.jpeg"
    },
    {
      id: '5',
      title: "Mining Pool Comparison: Which One Maximizes Your Returns?",
      excerpt: "A detailed analysis of the top mining pools and their fee structures",
      date: "20.01.2025",
      image: "/images/blog/blog5.jpeg"
    },
    {
      id: '6',
      title: "Beginner's Guide to Setting Up Your First Mining Rig",
      excerpt: "Step-by-step instructions for those new to the mining world",
      date: "15.01.2025",
      image: "/images/blog/blog6.jpeg"
    }
  ];

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const target = e.currentTarget;
    // 移除图片元素，显示父容器作为占位符
    target.style.display = 'none';
    // 将父元素的内容设置为 BITENGINE Blog
    const parent = target.parentElement;
    if (parent) {
      parent.textContent = 'BITENGINE Blog';
    }
  };
  
  return (
    <OverviewContainer>
      <AnimatedSection direction="up" delay={0.3}>
        <BlogList>
          {blogItems.map(blog => (
            <BlogCard key={blog.id}>
              <div className="blog-image">
                <img 
                  src={blog.image} 
                  alt={blog.title} 
                  onError={handleImageError}
                />
              </div>
              <div className="blog-content">
                <div className="blog-date">{blog.date}</div>
                <h3 className="blog-title">{blog.title}</h3>
                <p className="blog-excerpt">{blog.excerpt}</p>
                <Link to={`/news/${blog.id}`} className="blog-link">
                  {t('common.readMore')}
                </Link>
              </div>
            </BlogCard>
          ))}
        </BlogList>
      </AnimatedSection>
      
      <AnimatedSection direction="up" delay={0.4}>
        <LoadMoreButton>
          {t('common.loadMore')}
        </LoadMoreButton>
      </AnimatedSection>
    </OverviewContainer>
  );
};

export default NewsOverview; 